import {
  UserPlus, BookOpen, Send, Volume2, BarChart3, CreditCard, Sparkles, Printer,
  School, Ticket, Mail, Users, Building2, Layers, Palette, Link2, Gift, ShieldCheck,
} from "lucide-react";

// Concise 5-8 step "how to use" guides per role. Shared by the /help page and the
// first-login welcome tour so content stays in one place.
export const TUTORIALS = {
  parent: {
    label: "Parents",
    icon: Users,
    color: "#0284C7",
    tagline: "Set your child up and watch them learn.",
    steps: [
      { icon: UserPlus, title: "Add your child", body: "From My Students, create an account for each child with a username and password they'll use to log in." },
      { icon: BookOpen, title: "Turn on lessons", body: "Open your Library and enable the subjects and lessons you want your child to see. Nothing shows until you enable it." },
      { icon: Send, title: "Assign or explore", body: "Assign specific lessons to a child, or let them freely pick from the Subject Hub on their home screen." },
      { icon: Volume2, title: "Independent mode", body: "Toggle \"Read to me\" (autoplay) for pre-readers so lessons are read aloud and advance hands-free." },
      { icon: BarChart3, title: "Track progress", body: "Open a child's report to see mastery, points and stickers earned across every subject." },
      { icon: CreditCard, title: "Manage your plan", body: "Upgrade any time from Plans or My Plan to unlock the full curriculum." },
    ],
  },
  teacher: {
    label: "Teachers",
    icon: BookOpen,
    color: "#0D9488",
    tagline: "Build a class and run lessons in minutes.",
    steps: [
      { icon: UserPlus, title: "Add your students", body: "Create student logins (username + password) from My Students — each child gets their own account." },
      { icon: BookOpen, title: "Enable your Library", body: "Switch on the subjects and lessons your class should see. Students only see what you enable." },
      { icon: Sparkles, title: "Generate a lesson", body: "In AI Lessons, describe what you want and publish a custom lesson tailored to your students." },
      { icon: Send, title: "Assign lessons", body: "Send any lesson to specific students — it appears in their \"Assigned to you\" section." },
      { icon: Printer, title: "Print worksheets", body: "Turn any lesson into a printable worksheet, with an optional answer key toggle." },
      { icon: Volume2, title: "Independent mode", body: "Toggle \"Read to me\" per student for hands-free, read-aloud learning." },
      { icon: BarChart3, title: "Review progress", body: "Track per-student completion in the Assignments tab and each student's report." },
    ],
  },
  school_admin: {
    label: "Schools",
    icon: School,
    color: "#0891B2",
    tagline: "Give every teacher a seat under one plan.",
    steps: [
      { icon: CreditCard, title: "Activate your School plan", body: "Buy the Individual School plan from Plans to unlock teacher seats you can invite educators into." },
      { icon: School, title: "Name your school", body: "Set the name your teachers and families will see across the platform." },
      { icon: Mail, title: "Invite teachers", body: "Send an email invite (via your SMTP) or share an invite link. One seat = one teacher." },
      { icon: Ticket, title: "Manage seats", body: "Watch your seat meter. Revoke a pending invite anytime to reclaim that seat." },
      { icon: ShieldCheck, title: "Teachers inherit access", body: "Invited teachers automatically get full access under your school's plan — no separate billing." },
      { icon: Mail, title: "Set up email (optional)", body: "Add your SMTP details so invites are sent from your own school's address." },
    ],
  },
  district_admin: {
    label: "Districts",
    icon: Building2,
    color: "#9333EA",
    tagline: "Distribute a seat pool across many schools.",
    steps: [
      { icon: CreditCard, title: "Activate your District plan", body: "Buy the School District plan from Plans to get a pool of teacher seats to allocate." },
      { icon: Building2, title: "Name your district", body: "Set your district name — schools you add will live under it." },
      { icon: School, title: "Add schools", body: "Create each school and give it a teacher-seat quota drawn from your district pool." },
      { icon: Layers, title: "Allocate seats", body: "The sum of all school quotas can't exceed your pool. Adjust each school's seats anytime." },
      { icon: Mail, title: "Invite school admins", body: "Send an admin invite (email or link) for each school so principals can manage their teachers." },
      { icon: BarChart3, title: "Track usage", body: "See seats used per school right from your district dashboard." },
    ],
  },
  reseller: {
    label: "Resellers",
    icon: Gift,
    color: "#E05A47",
    tagline: "Brand the platform and gift access to clients.",
    steps: [
      { icon: Palette, title: "Set up your brand", body: "Add your name, logo, colors and a URL slug to power your own branded landing page." },
      { icon: CreditCard, title: "Buy your seat plan", body: "Activate a reseller plan — your seat count sets how many redemption codes you can create." },
      { icon: Ticket, title: "Generate codes", body: "Create redemption codes (1 code = 1 seat) to gift families free full access." },
      { icon: Link2, title: "Share your link", body: "Send clients your branded /r/your-slug page along with their code to redeem." },
      { icon: Ticket, title: "Manage seats", body: "Revoke an unredeemed code anytime to free up a seat for someone else." },
      { icon: ShieldCheck, title: "Clients inherit your brand", body: "Redeemed teachers and parents see your branding and get full access automatically." },
    ],
  },
};

export const TUTORIAL_ORDER = ["parent", "teacher", "school_admin", "district_admin", "reseller"];

// Roles that get the first-login welcome tour.
export const TOUR_ROLES = TUTORIAL_ORDER;
